import { resolvePostLoginRedirectTarget } from "@src/config/routes"
import { assign, setup } from "xstate"

export interface AppRouterContext {
  pathname: string
  searchRedirect: string | null
  routeIntent: string | null
}

export type AppRouterEvent =
  | {
      type: "SYNC_CONTEXT"
      pathname: string
      searchRedirect: string | null
      routeIntent: string | null
    }
  | { type: "NAVIGATE_TO_LOGIN" }
  | { type: "NAVIGATE_FROM_LOGIN_IF_NEEDED" }
  | { type: "NAVIGATE_TO_BANNED" }

/**
 * Router command/state machine used by bootstrap.
 *
 * Boundaries:
 * - Owns synchronized route context: `pathname`, `searchRedirect`, `routeIntent`.
 * - Resolves navigation targets from context (URL computation lives here, not in the provider).
 * - Executes a single injected `navigate(url)` side effect.
 *
 * Injected via `.provide()` in the provider: `navigate`.
 */
export const appRouterMachine = setup({
  types: {} as {
    context: AppRouterContext
    input: AppRouterContext
    events: AppRouterEvent
  },
  actions: {
    syncContext: assign(({ event }) => {
      if (event.type !== "SYNC_CONTEXT") return {}
      return {
        pathname: event.pathname,
        searchRedirect: event.searchRedirect,
        routeIntent: event.routeIntent,
      }
    }),
    navigate: (_, _params: { url: string }) => {},
  },
}).createMachine({
  id: "app-router",
  context: ({ input }) => input,
  on: {
    SYNC_CONTEXT: {
      actions: "syncContext",
    },
    NAVIGATE_TO_LOGIN: {
      actions: {
        type: "navigate",
        params: ({ context }) => ({
          url: `/login${context.routeIntent ? `?redirect=${encodeURIComponent(context.routeIntent)}` : ""}`,
        }),
      },
    },
    NAVIGATE_FROM_LOGIN_IF_NEEDED: {
      guard: ({ context }) => context.pathname === "/login",
      actions: {
        type: "navigate",
        params: ({ context }) => ({
          url: resolvePostLoginRedirectTarget(context.searchRedirect),
        }),
      },
    },
    NAVIGATE_TO_BANNED: {
      actions: {
        type: "navigate",
        params: { url: "/banned" },
      },
    },
  },
})
